import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, minsToHhMm, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyShuruDashboard } from './companyShuruApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import {
  CHAT_DEPTH_RAMP,
  MESSAGE_DEPTH_RAMP,
  FEEDBACK_RAMP,
  POSITIVITY_RAMP,
  CHANNEL_RAMP,
  moodColors
} from './shuruTheme';

/**
 * Shuru → Dashboard.
 *
 * Shuru is the in-app AI companion, and the only feature in the panel whose
 * record is a conversation rather than a completed activity. So this page is
 * deliberately outcome-led: how many people reached out, how they felt
 * afterwards, and what they were carrying — with volume as supporting evidence
 * rather than the headline.
 *
 * Three numbers carry a caveat that is stated wherever they appear:
 *  - "Felt better" comes from a skippable end-of-chat prompt, so its rate is
 *    among rated chats, not among all chats.
 *  - Time is measured from a person's first message of the day to their last,
 *    so it is time in conversation; two chats in one day read as one span.
 *  - The hour-of-day chart is in UTC.
 *
 * The themes panels are the reason this section exists, and also the reason it
 * stays aggregate: they are company-wide counts of PEOPLE per statement and are
 * never available per user.
 *
 * `fetcher` is a prop so a super-admin Shuru hub can render this page unscoped;
 * the builders behind both are the same.
 */
const ShuruDashboard = ({ fetcher = fetchCompanyShuruDashboard, populationLabel = 'users' }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const chatsTrend = stats?.chatsTrend || [];
  const messagesTrend = stats?.messagesTrend || [];
  const weekdayChats = stats?.weekdayChats || [];
  const hourlyChats = stats?.hourlyChats || [];
  const feedbackSplit = stats?.feedbackSplit || [];
  const feedbackChannels = stats?.feedbackChannels || [];
  const positivitySplit = stats?.positivitySplit || [];
  const chatDepth = stats?.chatDepth || [];
  const messageDepth = stats?.messageDepth || [];
  const moodCheckIns = stats?.moodCheckIns || [];
  const positiveThemes = stats?.positiveThemes || [];
  const negativeThemes = stats?.negativeThemes || [];

  // What share of the population ever opened Shuru.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.chatters / stats.totalUsers) * 100) : null;

  /**
   * Chats and messages on one chart, two axes: chats rising without messages
   * rising means people are opening Shuru and saying one thing, which is the
   * opposite conclusion from the chats line alone.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 4] },
    fill: { type: ['gradient', 'solid'], gradient: AREA_FILL.gradient },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(chatsTrend.length), tickAmount: 6 },
    yaxis: [
      { seriesName: 'Chats', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Messages',
        opposite: true,
        labels: { formatter: (val) => formatBigNumber(val) }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} chats` },
        { formatter: (val) => `${val.toLocaleString()} messages` }
      ]
    }
  };

  /**
   * Chats started by hour. The shape matters more than any single bar: a hump
   * after 22:00 is people reaching out at night, which is a wellbeing signal a
   * daily total hides completely.
   */
  const hourlyOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.pink],
    plotOptions: { bar: { borderRadius: 3, columnWidth: '70%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: hourlyChats.map((slot) => slot.label), tickAmount: 8 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} chats started` } }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayChats.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} chats` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Talking"
          value={stats ? formatBigNumber(stats.chatters) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.plays}
          title="Chats"
          value={stats ? formatBigNumber(stats.chats) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Messages Sent"
          value={stats ? formatBigNumber(stats.userMessages) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Time in Conversation"
          value={stats ? minsToHhMm(stats.totalTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Felt Better"
          value={stats?.feedbackResponses > 0 ? `${stats.feltBetterRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Messages per Chat"
          value={stats ? stats.messagesPerChat : '-'}
        />
      </div>

      {/* ---------- Outcome and depth ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {/* The prompt at the end of a chat is skippable, so this counts answers
            given — never all chats. */}
        <MiniDonutCard
          title="How They Felt After"
          subtitle="Answers to the end-of-chat prompt"
          data={feedbackSplit}
          colors={FEEDBACK_RAMP}
          unit="answers"
          totalLabel="Rated"
        />
        <MiniDonutCard
          title="Conversation Depth"
          subtitle="Chats started per person"
          data={chatDepth}
          colors={CHAT_DEPTH_RAMP}
          unit="people"
          totalLabel="People"
        />
        <MiniDonutCard
          title="Chat Length"
          subtitle="Messages sent per chat day"
          data={messageDepth}
          colors={MESSAGE_DEPTH_RAMP}
          unit="chat days"
          totalLabel="Chat days"
        />
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Chats & Messages" subtitle="Per day — last 30 days">
          {chatsTrend.length > 0 ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Chats', type: 'area', data: chatsTrend },
                { name: 'Messages', type: 'line', data: messagesTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No conversations recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="When People Reach Out" subtitle="Chats started by hour of day (UTC)">
          {hourlyChats.some((slot) => slot.chats > 0) ? (
            <Chart
              type="bar"
              height={380}
              width="100%"
              series={[{ name: 'Chats', data: hourlyChats.map((slot) => slot.chats) }]}
              options={hourlyOptions}
            />
          ) : (
            <PanelEmpty>No conversations recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Reach and arrival ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Shuru Reach"
          subtitle={`Share of ${populationLabel} who have talked to Shuru`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.chatters)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        <Panel title="Chats by Weekday" subtitle="Which days people open up">
          {weekdayChats.some((day) => day.chats > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Chats', data: weekdayChats.map((day) => day.chats) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No conversations recorded yet.</PanelEmpty>
          )}
        </Panel>

        {/* The mood people pick before opening Shuru — coloured by tone, so a
            mix leaning pink means people mostly arrive on a hard day. */}
        <MiniDonutCard
          title="How People Arrive"
          subtitle="Mood selected before a chat"
          data={moodCheckIns}
          colors={moodColors(moodCheckIns)}
          unit="check-ins"
          totalLabel="Check-ins"
          legendLimit={4}
        />
      </div>

      {/* ---------- Themes ----------
          Shuru matches each message against a fixed list of workplace
          statements. Ranked by how many PEOPLE a statement matched for, so one
          heavy user cannot invent a company-wide theme, and only ever shown at
          company level — never on a user's profile. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="What's Working" subtitle={`Positive themes across your ${populationLabel}`}>
          {positiveThemes.length > 0 ? (
            <ProgressList items={positiveThemes} unit="people" color={CHART_COLORS.green} />
          ) : (
            <PanelEmpty>No themes matched yet.</PanelEmpty>
          )}
        </Panel>

        <Panel
          title="What's Weighing on People"
          subtitle={`Negative themes across your ${populationLabel}`}
        >
          {negativeThemes.length > 0 ? (
            <ProgressList items={negativeThemes} unit="people" color={CHART_COLORS.pink} />
          ) : (
            <PanelEmpty>No themes matched yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Supporting detail ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Message Tone"
          subtitle="Sentiment of the messages people send"
          data={positivitySplit}
          colors={POSITIVITY_RAMP}
          unit="messages"
          totalLabel="Messages"
        />
        <MiniDonutCard
          title="Text or Voice"
          subtitle="How rated chats were held"
          data={feedbackChannels}
          colors={CHANNEL_RAMP}
          unit="chats"
          totalLabel="Rated"
        />

        <Panel title="Conversation Summary" subtitle="The raw counts behind this page">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Chats per person</td>
              <td className="py-2 text-right">{stats ? stats.avgChatsPerUser : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Time per person</td>
              <td className="py-2 text-right">
                {stats ? minsToHhMm(stats.avgTimePerUserMins) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Avg time per chat day</td>
              <td className="py-2 text-right">{stats ? minsToHhMm(stats.avgDailyMins) : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Shuru replies</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.botMessages) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Chats rated</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.feedbackResponses) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last chat</td>
              <td className="py-2 text-right">{stats?.lastChatDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>
    </div>
  );
};

ShuruDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default ShuruDashboard;
